import { useState, useEffect } from 'preact/hooks';
import { api } from '../../api/client';
import { scStyles } from './styles';
import type { SkillMeta } from './types';
import { T } from '../../styles/tokens';
import { Button } from '../ui';

interface Props {
  skill: SkillMeta | null;
  onClose: () => void;
  onInstall?: (skill: SkillMeta) => void;
}

export function SkillDetailPanel({ skill, onClose, onInstall }: Props) {
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!skill) return;
    setLoading(true);
    setContent('');
    if (skill.tier === 'tier1') {
      api.get<{ content: string }>(`/cloud/skills-pack/tier1/${encodeURIComponent(skill.name)}`)
        .then((r: any) => setContent(r?.content || r?.data?.content || 'Sem conteúdo disponível'))
        .catch(() => setContent('Erro ao carregar detalhes'))
        .finally(() => setLoading(false));
    } else {
      setContent(skill.description_pt || skill.description || 'Sem detalhes disponíveis');
      setLoading(false);
    }
  }, [skill]);

  if (!skill) return null;

  return (
    <>
      {/* Backdrop */}
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: T.bgOverlay, zIndex: 1000 }} />
      {/* Panel */}
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: '480px', maxWidth: '90vw',
        background: T.bg, borderLeft: `1px solid ${T.purpleBgStrong}`,
        animation: 'slideIn 0.3s ease', zIndex: 1001, display: 'flex', flexDirection: 'column',
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{ padding: '20px 24px', borderBottom: `1px solid ${T.purpleBgStrong}`, display: 'flex', alignItems: 'center', gap: '12px' }}>
 <span style={{ fontSize: '36px' }}>{skill.emoji || ''}</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '18px', fontWeight: 700, color: T.text }}>{skill.name}</div>
            <div style={{ fontSize: '12px', color: T.textSecondary, marginTop: '2px' }}>{skill.category || 'Geral'}</div>
          </div>
          <Button variant="ghost" onClick={onClose} style={{ background: 'none', border: 'none', color: T.textSecondary, fontSize: '20px', cursor: 'pointer', padding: '4px 8px' }}></Button>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: '20px 24px' }}>
          {loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {[1,2,3].map(i => (
                <div key={i} style={{ height: '16px', borderRadius: '4px', background: `linear-gradient(90deg, ${T.purpleBg} 25%, ${T.purpleBgStrong} 50%, ${T.purpleBg} 75%)`, backgroundSize: '200% 100%', animation: 'skeletonShimmer 1.5s infinite' }} />
              ))}
            </div>
          ) : (
            <pre style={{ fontSize: '13px', color: T.textSecondary, lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'inherit', margin: 0 }}>
              {content}
            </pre>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '16px 24px', borderTop: `1px solid ${T.purpleBgStrong}`, display: 'flex', gap: '12px' }}>
          {skill.requires_api_key ? (
 <div style={{ ...scStyles.badgeRecommended, flex: 'none' }}>Requer API Key</div>
          ) : (
 <div style={{ ...scStyles.badgeVerified, flex: 'none' }}>Sem API Key</div>
          )}
          <div style={{ flex: 1 }} />
          <Button variant="ghost"
            onClick={() => onInstall?.(skill)}
            style={{
              padding: '10px 24px', borderRadius: '10px', border: 'none',
              ...scStyles.gradientPurple, color: T.text, fontSize: '14px', fontWeight: 600, cursor: 'pointer',
            }}
          >
            Instalar
          </Button>
        </div>
      </div>
    </>
  );
}
